import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { client } from "../net/client";
import { useSignal } from "../net/signal";
import { voice } from "../net/voice";
import { BotIcon } from "../ui/BotIcon";
import { ChatGlyph, UnreadBadge } from "../ui/Chat";
import { avatarSrc } from "./avatar";
import { fa } from "./cards";
import { CHAT_TOGGLE, cannedText } from "./chat";
import { toLocal } from "../board/state";
import { CountryFlag } from "../ui/CountryFlag";

/**
 * صندلیِ گرد و گفت‌وگوی میزهای تخته‌ای (دومینو، منچ) — همتای RoundSeat و BoardChat ِ اپ.
 *
 * صندلی: چهره‌ی گرد با حلقه‌ی رنگِ صندلی، هاله و حلقه‌ی نوبت (برای خودت ثانیه‌شمار با ثانیه‌ها روی چهره)،
 * قرصِ نام و یک خطِ زیر. گفت‌وگو: دکمه‌ی ایموجی (چهار واکنش) و دکمه‌ی چت که برگه‌ی چتِ میز را باز می‌کند؛
 * و حبابِ آخرین پیامِ هر نشسته کنارِ چهره‌اش.
 */

/** واکنش‌های یک‌لمسی: کلیدِ chat.Canned ِ سرور → نشانه‌ی کشیده (همان اپ: tools/mafia_react_art.py). */
export const REACT_ART: Record<string, string> = { r_laugh: "laugh", r_shock: "shock", clap: "clap", r_sad: "sad" };
export const REACTS = ["r_laugh", "r_shock", "clap", "r_sad"] as const;
export const reactSrc = (k: string) => `/art/mafia/react/${REACT_ART[k]}.svg`;

export type SeatBubble = { key: string; text: string; art?: string };

/** حبابِ چتِ هر صندلی: آخرین پیامِ نشسته‌ها، ۲٫۶ ثانیه. واکنش نشانه است، بقیه متن. */
export function useSeatBubbles(n: number, me: number): Map<number, SeatBubble> {
  const msgs = useSignal(client.chat);
  const [shown, setShown] = useState(new Map<number, SeatBubble & { until: number }>());
  const seen = useRef(msgs.length);
  useEffect(() => {
    if (msgs.length < seen.current) seen.current = 0;
    const fresh = msgs.slice(seen.current);
    seen.current = msgs.length;
    if (fresh.length === 0) return;
    setShown((cur) => {
      const next = new Map(cur);
      for (const msg of fresh) {
        if (msg.seat < 0 || msg.seat >= n) continue;
        const text = msg.canned ? cannedText(msg.canned) : msg.text;
        if (!text.trim()) continue;
        next.set(toLocal(msg.seat, me, n), { key: `${msg.atMs}-${msg.playerId}`, text,
          art: msg.canned && REACT_ART[msg.canned] ? reactSrc(msg.canned) : undefined, until: performance.now() + 2600 });
      }
      return next;
    });
  }, [msgs, me, n]);
  const [, tick] = useState(0);
  useEffect(() => {
    if (shown.size === 0) return;
    const soon = Math.min(...[...shown.values()].map((b) => b.until)) - performance.now();
    const t = setTimeout(() => {
      setShown((cur) => new Map([...cur].filter(([, b]) => b.until > performance.now() + 20)));
      tick((x) => x + 1);
    }, Math.max(20, soon + 20));
    return () => clearTimeout(t);
  }, [shown]);
  return shown;
}

export function Bubble({ b }: { b?: SeatBubble }) {
  if (!b) return null;
  return b.art
    ? <i key={b.key} className="rs-bub art" role="img" aria-label={b.text}><img src={b.art} alt="" /></i>
    : <i key={b.key} className="rs-bub">{b.text}</i>;
}

/**
 * صدای زنده روی صندلیِ تخته‌ای — همان حلقه و نشانِ صندلی‌های ورق (scene/table.ts) و VoiceSeatFx ِ اپ.
 * کلاس‌های `vox-in` (در اتاقِ صداست)، `vox-on` (همین حالا حرف می‌زند) و `vox-muted` (میکروفونش بسته)؛
 * خالی برای کسی که در اتاقِ صدا نیست، از جمله ربات‌ها و هر میزی که صدا ندارد. فقط نمایش است.
 */
export function useVoiceSeat(playerId: string | undefined): string {
  const speaking = useSignal(voice.speaking), members = useSignal(voice.members), silenced = useSignal(voice.silenced);
  if (!playerId || !members.has(playerId)) return "";
  return ` vox-in${speaking.has(playerId) ? " vox-on" : ""}${silenced.has(playerId) ? " vox-muted" : ""}`;
}

/** نورِ پشتِ چهره و نشانِ میکروفون — فقط وقتی صندلی در اتاقِ صداست. */
export function VoiceMarks() {
  return (
    <>
      <i className="vox" aria-hidden="true" />
      <i className="vmic" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
          <rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21" />
        </svg>
      </i>
    </>
  );
}

/**
 * یک صندلی. `secs` > ۰ یعنی ثانیه‌شمارِ خودت (`frac` ۱ پر)؛ نوبتِ بی ثانیه‌شمار حلقه‌ی طلاییِ تپنده می‌گیرد.
 * `extra` روی چهره کشیده می‌شود (شمارِ مهره‌ها، نشانِ تیم)؛ `sub` خطِ زیرِ نام. `pid` برای صدای زنده.
 */
export function RoundSeat({ name, avatar, country, bot, me, color, turn, secs, frac, sub, extra, bubble, cls, pid }:
  { name: string; avatar?: string; country?: string; bot?: boolean; me: boolean; color: string; turn: boolean; secs: number; frac: number;
    sub: ReactNode; extra?: ReactNode; bubble?: SeatBubble; cls?: string; pid?: string }) {
  const low = secs > 0 && frac < 0.25;
  const vox = useVoiceSeat(pid);
  return (
    <div className={`rseat${me ? " me" : ""}${turn ? " turn" : ""}${cls ? ` ${cls}` : ""}${vox}`}
         style={{ "--tok": color, "--f": turn ? (secs > 0 ? frac : 1) : 0 } as CSSProperties}
         aria-label={`${name}${turn ? " — نوبت" : ""}`}>
      <div className={`av${secs > 0 ? " timed" : ""}${low ? " low" : ""}`}>
        {vox && <VoiceMarks />}
        <i className="ring" aria-hidden="true" />
        <img src={avatarSrc(avatar)} alt="" />
        <CountryFlag code={country} />
        {secs > 0 && <b className="sec" aria-label={`${fa(secs)} ثانیه`}>{fa(secs)}</b>}
        {extra}
      </div>
      <div className="pill"><b>{bot && !me && <BotIcon size={12} />}{name}</b></div>
      <div className="sc">{sub}</div>
      <Bubble b={bubble} />
    </div>
  );
}

/** دکمه‌ی ایموجی (چهار واکنش) و دکمه‌ی چت (برگه‌ی چتِ میز در App، با عددِ نخوانده). */
export function BoardChatButtons({ onReact }: { onReact: (key: string) => void }) {
  const [pop, setPop] = useState(false);
  return (
    <div className="bchat">
      <button className={`bchat-b emo${pop ? " on" : ""}`} onClick={() => setPop((v) => !v)} aria-label="ایموجی" aria-expanded={pop}>
        <img src={reactSrc("r_laugh")} alt="" />
      </button>
      <button className="bchat-b chat" onClick={() => { setPop(false); window.dispatchEvent(new Event(CHAT_TOGGLE)); }} aria-label="چت میز">
        <ChatGlyph />
        <UnreadBadge />
      </button>
      {pop && (
        <div className="bchat-pop" role="group" aria-label="ایموجی">
          {REACTS.map((k) => (
            <button key={k} onClick={() => { setPop(false); onReact(k); }} aria-label={cannedText(k)}>
              <img src={reactSrc(k)} alt="" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
